import { useState } from 'react';
import { StyleSheet, View } from 'react-native';

import {
  IconButton,
  IconClose,
  IconErrorFill,
  IconInfoFill,
  IconWarningFill,
  Text,
  XStack,
  YStack,
  tokens,
} from '@fhs/ui';
import { CLOSE_INLINE_ALERT } from '@fhs-legacy/frontend/src/utils/test-ids';

interface EnhancedInlineAlertProps {
  title?: string;
  status: 'error' | 'warning' | 'info';
  message: React.ReactNode | string | undefined;
  isDismissable?: boolean;
  onDismiss?: () => void;
  action?: React.ReactNode;
  style?: any;
  testID?: string;
}

export function EnhancedInlineAlert({
  title,
  status,
  message,
  isDismissable = false,
  onDismiss,
  action,
  style,
  testID,
}: EnhancedInlineAlertProps) {
  const [isVisible, setIsVisible] = useState(true);

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  if (!isVisible) {
    return null;
  }

  const backgroundColorByStatus = {
    info: tokens.colors.$houseLight,
    warning: tokens.colors.$warningBackground,
    error: tokens.colors.$errorBackground,
  };

  const iconByStatus = {
    info: <IconInfoFill size={20} color={tokens.colors.$houseDark} />,
    warning: <IconWarningFill size={20} color={tokens.colors.$warning} />,
    error: <IconErrorFill size={20} color={tokens.colors.$error} />,
  };

  const backgroundColor = backgroundColorByStatus[status];
  const icon = iconByStatus[status];

  return (
    <View testID={testID} style={[styles.container, { backgroundColor }, style]}>
      <XStack style={styles.alertContent}>
        <View style={styles.iconContainer}>{icon}</View>
        <YStack style={styles.textContainer}>
          {title && <Text style={styles.title}>{title}</Text>}
          <Text style={styles.message}>{message}</Text>
          {action && <View style={styles.actionContainer}>{action}</View>}
        </YStack>
        {isDismissable && (
          <IconButton
            type="ghost"
            onPress={handleDismiss}
            style={styles.dismissButton}
            testID={CLOSE_INLINE_ALERT}
          >
            <IconClose size={20} />
          </IconButton>
        )}
      </XStack>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  alertContent: {
    gap: 8,
    alignItems: 'flex-start',
  },
  iconContainer: {
    marginTop: 2,
  },
  textContainer: {
    flex: 1,
    gap: 4,
    marginLeft: 8,
  },
  title: {
    fontSize: 14,
    fontWeight: 'bold',
    lineHeight: 20,
  },
  message: {
    fontSize: 12,
    lineHeight: 16.8,
  },
  actionContainer: {
    marginTop: 4,
  },
  dismissButton: {
    padding: 4,
    marginLeft: 8,
  },
});
